import { Children, isValidElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { cx } from '@/utils/cx';

import styles from './Category.module.scss';
import { ActionProvider } from './CategoryContext';
import { CategoryDivider } from './Divider/CategoryDivider';
import { CategoryGroup } from './Group/CategoryGroup';

export interface CategoryProps {
  children: ReactNode;
  /** 모든 GroupTitle·Item 클릭 시 호출되는 중앙 핸들러 */
  onAction?: (action: string, meta: Record<string, unknown>, originalEvent: React.MouseEvent) => void;
  /** 제어형: 현재 활성 action. 지정 시 내부 상태를 사용하지 않음. */
  activeAction?: string;
  /** 비제어형: 초기 활성 action. 이후 클릭으로 내부 상태 자동 전환. */
  defaultActiveAction?: string;
  /** 접근성 라벨 (기본값 '전체 카테고리') */
  'aria-label'?: string;
  className?: string;
}

/** dev 전용: action 중복 검증 */
function validateUniqueActions(children: ReactNode): void {
  if (process.env.NODE_ENV === 'production') return;

  const actions: string[] = [];

  function collect(nodes: ReactNode) {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const props = child.props as Record<string, unknown>;
      if (typeof props.action === 'string') {
        actions.push(props.action);
      }
      if (props.children) {
        collect(props.children as ReactNode);
      }
    });
  }

  collect(children);

  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const action of actions) {
    if (seen.has(action)) duplicates.add(action);
    seen.add(action);
  }

  if (duplicates.size > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Category] 중복된 action이 감지되었습니다: ${[...duplicates].map((a) => `"${a}"`).join(', ')}. ` +
        'action은 고유해야 합니다. activeAction 판단과 onAction 식별에 문제가 발생할 수 있습니다.',
    );
  }
}

/** 인접한 Category.Group 사이에 Divider를 자동 삽입 */
function interleaveWithDividers(children: ReactNode): ReactNode[] {
  const items = Children.toArray(children);
  const result: ReactNode[] = [];

  for (let i = 0; i < items.length; i++) {
    result.push(items[i]);

    if (i < items.length - 1) {
      const current = items[i];
      const next = items[i + 1];
      // 직접 참조 비교 — displayName 문자열 비교는 minification mangling 위험 및
      // displayName 미선언 시 일반 HTML element 와 false-match 위험이 있어 사용하지 않는다.
      const isCurrentGroup = isValidElement(current) && current.type === CategoryGroup;
      const isNextGroup = isValidElement(next) && next.type === CategoryGroup;

      if (isCurrentGroup && isNextGroup) {
        result.push(<CategoryDivider key={`divider-${i}`} />);
      }
    }
  }

  return result;
}

export const Category = ({
  children,
  className,
  onAction,
  activeAction: controlledActive,
  defaultActiveAction,
  'aria-label': ariaLabel = '전체 카테고리',
}: CategoryProps) => {
  const isControlled = controlledActive !== undefined;
  const [uncontrolledActive, setUncontrolledActive] = useState(defaultActiveAction);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (controlledActive !== undefined && defaultActiveAction !== undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Category] activeAction과 defaultActiveAction을 동시에 사용할 수 없습니다. activeAction(제어형)이 우선 적용됩니다.',
      );
    }
  }, [controlledActive, defaultActiveAction]);

  useEffect(() => {
    validateUniqueActions(children);
  }, [children]);

  const currentActive = isControlled ? controlledActive : uncontrolledActive;

  const handleAction = useCallback(
    (action: string, meta: Record<string, unknown>, e: React.MouseEvent) => {
      if (!isControlled) {
        setUncontrolledActive(action);
      }
      onAction?.(action, meta, e);
    },
    [isControlled, onAction],
  );

  const contextValue = useMemo(
    () => ({ onAction: handleAction, activeAction: currentActive }),
    [handleAction, currentActive],
  );

  return (
    <ActionProvider value={contextValue}>
      <nav aria-label={ariaLabel} className={cx(styles.root, className)} data-core-ui-category="root">
        {interleaveWithDividers(children)}
      </nav>
    </ActionProvider>
  );
};
